// Link to the robot's own backend (Misti_HW Backend, Socket.IO on port 8008).
//
// Three channels, sharing one connection:
//   /message    text from the phone app for Misti to say (HomePage speaks it)
//   /led        the LED ring: send "t1:#RRGGBB", every page sees led_status
//   /direction  drive commands: send "F" "B" "L" "R" "S", every page sees
//               direction_update
// The camera (/video) and log (/logs) channels stay off: joining /video makes
// the Pi start streaming camera frames to this page.
//
// Where the backend is: see services/robotUrl.
//
// Loaded from the https site, Chrome may ask once whether the page may reach
// devices on the local network - allow it, or app messages never arrive.

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Manager } from "socket.io-client";
import { robotUrl } from "services/robotUrl";

const CHANNELS = ["message", "led", "direction"];

const SocketContext = createContext({ chatSocket: null, connected: {} });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
	const [sockets, setSockets] = useState({});
	const [connected, setConnected] = useState({});
	const [lastLed, setLastLed] = useState(null);
	const [lastDirection, setLastDirection] = useState(null);

	useEffect(() => {
		const url = robotUrl();
		const manager = new Manager(url, {
			transports: ["websocket"],
			// Opened below, once every listener is attached.
			autoConnect: false,
			// The backend may start after the page (power-on) or restart; keep
			// trying, but no more often than every 10 s.
			reconnectionDelayMax: 10000,
		});

		// One warning per outage, not one per retry.
		let warned = false;
		manager.on("open", () => {
			warned = false;
			console.log(`[robot] connected to ${url}`);
		});
		manager.on("error", (err) => {
			if (warned) return;
			warned = true;
			console.warn(`[robot] cannot reach ${url} (${err.message}) - retrying in the background`);
		});

		const made = {};
		CHANNELS.forEach((name) => {
			const s = manager.socket(`/${name}`);
			s.on("connect", () => setConnected((c) => ({ ...c, [name]: true })));
			s.on("disconnect", () => setConnected((c) => ({ ...c, [name]: false })));
			made[name] = s;
		});
		made.led.on("led_status", setLastLed);
		made.direction.on("direction_update", setLastDirection);
		CHANNELS.forEach((name) => made[name].connect());

		setSockets(made);
		return () => {
			CHANNELS.forEach((name) => made[name].disconnect());
			manager.removeAllListeners();
			setSockets({});
			setConnected({});
		};
	}, []);

	const emitOn = useCallback(
		(name, event, data) => {
			const s = sockets[name];
			if (!s || !s.connected) return false;
			s.emit(event, data);
			return true;
		},
		[sockets]
	);

	const sendMsgMessage = useCallback((text) => emitOn("message", "send_message", text), [emitOn]);
	const sendLedMessage = useCallback((command) => emitOn("led", "led_control", command), [emitOn]);
	const sendDirection = useCallback((cmd) => emitOn("direction", "direction", cmd), [emitOn]);

	return (
		<SocketContext.Provider
			value={{
				chatSocket: sockets.message || null,
				ledSocket: sockets.led || null,
				directionSocket: sockets.direction || null,
				connected,
				sendMsgMessage,
				sendLedMessage,
				sendDirection,
				lastLed,
				lastDirection,
			}}
		>
			{children}
		</SocketContext.Provider>
	);
};
